/**
 * Loader für den Apothekenfinder.
 *
 * Wird auf Hostseiten via:
 *   <script src="https://apothekenfinder.uebe.com/v1/loader.js" async></script>
 *   <apothekenfinder-widget site="visomat" center="50.11,8.68"></apothekenfinder-widget>
 *
 * Aufgaben:
 *   1. Custom Element registrieren (idempotent).
 *   2. Sofortiges Skeleton-Rendering im Shadow-Root (kein CLS).
 *   3. Bundle erst lazy nachladen, wenn das Element nahe Viewport ist.
 *   4. Embed-Token vor dem Mount holen.
 */

export {}; // Loader ist ein Modul (sonst keine `declare global`-Augmentation erlaubt).

interface WidgetOptions {
  site?: 'visomat' | 'domotherm';
  center?: string | { lat: number; lng: number };
  filters?: string[] | string;
  theme?: string | Record<string, string>;
}

interface WidgetHandle {
  update: (opts: Partial<WidgetOptions>) => void;
  destroy: () => void;
}

interface ApfGlobal {
  version: string;
  mount: (el: Element, opts?: WidgetOptions) => WidgetHandle;
}

interface MountModule {
  mount: (
    root: ShadowRoot,
    config: WidgetConfig,
    runtime: {
      token: string;
      manifest: Manifest;
      origin: string;
      brand: { primary: string; secondary: string } | null;
    },
  ) => WidgetHandle;
}

interface Manifest {
  version: string;
  files: { js: string; css?: string; loader: string };
}

interface WidgetConfig {
  site: 'visomat' | 'domotherm';
  center: { lat: number; lng: number } | null;
  filters: string[];
  theme: string | null;
}

const SCRIPT_TAG = (document.currentScript as HTMLScriptElement | null);
const ORIGIN = computeOrigin();
const TAG_NAME = 'apothekenfinder-widget';

function computeOrigin(): string {
  if (SCRIPT_TAG?.src) {
    try {
      return new URL(SCRIPT_TAG.src).origin;
    } catch {
      /* noop */
    }
  }
  return location.origin;
}

const SKELETON_CSS = `
:host {
  all: initial;
  display: block;
  min-height: 600px;
  contain: layout style;
  container-type: inline-size;
  font: 16px/1.5 system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #1a1a1a;
  --apf-accent: var(--brand-primary, #d6002a);
  --apf-accent-fg: var(--brand-on-primary, #ffffff);
  --apf-radius: var(--brand-radius, 12px);
  --apf-bg: var(--brand-bg, #ffffff);
  --apf-fg: #1a1a1a;
  --apf-muted: #6b7280;
  --apf-border: #e5e7eb;
  --apf-shadow: 0 8px 30px rgba(0,0,0,.08);
}
.apf-skeleton {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  min-height: 600px;
  border: 1px solid var(--apf-border);
  border-radius: var(--apf-radius);
  overflow: hidden;
  background: var(--apf-bg);
}
.apf-skeleton-bar {
  height: 56px;
  background: linear-gradient(90deg, #f3f4f6, #e5e7eb, #f3f4f6);
  background-size: 200% 100%;
  animation: apf-shimmer 1.4s infinite;
}
.apf-skeleton-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1px;
  background: var(--apf-border);
}
@container (min-width: 720px) {
  .apf-skeleton-body { grid-template-columns: 360px 1fr; }
}
.apf-skeleton-list, .apf-skeleton-map {
  background: linear-gradient(110deg, #f9fafb, #f3f4f6, #f9fafb);
  background-size: 200% 100%;
  animation: apf-shimmer 1.4s infinite;
}
@keyframes apf-shimmer { to { background-position: -200% 0; } }
`;

class ApothekenfinderWidget extends HTMLElement {
  static get observedAttributes() {
    return ['site', 'center', 'filters', 'theme'];
  }

  private _shadow: ShadowRoot;
  private _io?: IntersectionObserver;
  private _handle?: WidgetHandle;
  private _booted = false;

  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: 'open', delegatesFocus: true });
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(SKELETON_CSS);
    this._shadow.adoptedStyleSheets = [sheet];
    this._renderSkeleton();
  }

  connectedCallback() {
    this._observe();
  }

  disconnectedCallback() {
    this._io?.disconnect();
    this._handle?.destroy();
  }

  attributeChangedCallback() {
    if (this._handle) {
      // WidgetConfig kompatibel zu Partial<WidgetOptions> reduzieren.
      const cfg = this._readConfigFromAttrs();
      this._handle.update({
        site: cfg.site,
        center: cfg.center ?? undefined,
        filters: cfg.filters,
        theme: cfg.theme ?? undefined,
      });
    }
  }

  private _renderSkeleton() {
    this._shadow.innerHTML = `
      <div class="apf-skeleton" aria-busy="true" aria-label="Apothekenfinder lädt">
        <div class="apf-skeleton-bar"></div>
        <div class="apf-skeleton-body">
          <div class="apf-skeleton-list"></div>
          <div class="apf-skeleton-map"></div>
        </div>
      </div>
    `;
  }

  private _readConfigFromAttrs(): WidgetConfig {
    const site = (this.getAttribute('site') ?? 'visomat') as WidgetConfig['site'];
    const center = parseCenter(this.getAttribute('center'));
    const filters = (this.getAttribute('filters') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const theme = this.getAttribute('theme');
    return { site, center, filters, theme };
  }

  private _observe() {
    if (!('IntersectionObserver' in window)) {
      void this._boot();
      return;
    }
    this._io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            this._io?.disconnect();
            void this._boot();
            return;
          }
        }
      },
      { rootMargin: '200px' },
    );
    this._io.observe(this);
  }

  private async _boot() {
    if (this._booted) return;
    this._booted = true;
    try {
      const config = this._readConfigFromAttrs();
      const manifest = await fetchManifest();
      // Token + Brand-Theme parallel holen. Theme darf NIE den Boot brechen
      // (fetchVariantTheme schluckt Fehler → null → Site-Default-Farben).
      const [token, brand] = await Promise.all([
        fetchToken(),
        fetchVariantTheme(config.site),
      ]);
      const url = new URL(`/v1/${manifest.files.js}`, ORIGIN).toString();
      const cssUrl = manifest.files.css
        ? new URL(`/v1/${manifest.files.css}`, ORIGIN).toString()
        : null;

      // CSS als Constructable Stylesheet nachziehen
      if (cssUrl) {
        const cssText = await fetch(cssUrl, { credentials: 'omit' }).then((r) => r.text());
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(cssText);
        this._shadow.adoptedStyleSheets = [...this._shadow.adoptedStyleSheets, sheet];
      }

      const mod = (await import(/* @vite-ignore */ url)) as MountModule;
      this._handle = mod.mount(this._shadow, config, {
        token,
        manifest,
        origin: ORIGIN,
        brand,
      });
    } catch (err) {
      console.error('[apothekenfinder] boot failed', err);
      this._renderError();
    }
  }

  private _renderError() {
    this._shadow.innerHTML = `
      <div role="alert" style="padding:24px;border:1px solid var(--apf-border);
           border-radius:var(--apf-radius);background:#fff5f5;color:#9b1c1c;">
        Apothekenfinder konnte nicht geladen werden. Bitte Seite neu laden.
      </div>
    `;
  }
}

function parseCenter(raw: string | null): WidgetConfig['center'] {
  if (!raw) return null;
  const parts = raw.split(',').map((s) => Number.parseFloat(s.trim()));
  if (parts.length !== 2 || parts.some((n) => !Number.isFinite(n))) return null;
  const [lat, lng] = parts;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

async function fetchManifest(): Promise<Manifest> {
  const res = await fetch(new URL('/v1/manifest.json', ORIGIN).toString(), {
    credentials: 'omit',
  });
  if (!res.ok) throw new Error(`manifest ${res.status}`);
  return (await res.json()) as Manifest;
}

async function fetchToken(): Promise<string> {
  const res = await fetch(new URL('/v1/embed-token', ORIGIN).toString(), {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`embed-token ${res.status}`);
  const body = (await res.json()) as { token: string };
  return body.token;
}

/**
 * Holt die Brand-Farben der Variante. Fehler werden bewusst geschluckt
 * (→ null), damit das Widget bei einem Theme-Problem nicht bricht — der
 * Mount fällt dann auf die Site-Default-Farben zurück.
 */
async function fetchVariantTheme(
  slug: string,
): Promise<{ primary: string; secondary: string } | null> {
  try {
    const res = await fetch(
      new URL(`/v1/variant?slug=${encodeURIComponent(slug)}`, ORIGIN).toString(),
      { credentials: 'omit' },
    );
    if (!res.ok) return null;
    const body = (await res.json()) as {
      primary_color?: string | null;
      secondary_color?: string | null;
    };
    return {
      primary: body.primary_color ?? '',
      secondary: body.secondary_color ?? '',
    };
  } catch {
    return null;
  }
}

function register() {
  if (customElements.get(TAG_NAME)) return;
  customElements.define(TAG_NAME, ApothekenfinderWidget);
}

function autoInit() {
  if (!SCRIPT_TAG?.dataset.autoinit) return;
  const el = document.createElement(TAG_NAME);
  for (const attr of ['site', 'center', 'filters', 'theme']) {
    const v = SCRIPT_TAG.dataset[attr];
    if (v) el.setAttribute(attr, v);
  }
  if (SCRIPT_TAG.parentNode) {
    SCRIPT_TAG.parentNode.insertBefore(el, SCRIPT_TAG.nextSibling);
  } else {
    document.body.appendChild(el);
  }
}

function init() {
  register();
  autoInit();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

const apfApi: ApfGlobal = {
  version: '1.0.0',
  mount(target: Element, opts: WidgetOptions = {}): WidgetHandle {
    register();
    const el = document.createElement(TAG_NAME) as ApothekenfinderWidget;
    if (opts.site) el.setAttribute('site', opts.site);
    if (opts.center) {
      const c =
        typeof opts.center === 'string' ? opts.center : `${opts.center.lat},${opts.center.lng}`;
      el.setAttribute('center', c);
    }
    if (opts.filters) {
      const f = Array.isArray(opts.filters) ? opts.filters.join(',') : opts.filters;
      el.setAttribute('filters', f);
    }
    if (opts.theme) {
      el.setAttribute(
        'theme',
        typeof opts.theme === 'string' ? opts.theme : JSON.stringify(opts.theme),
      );
    }
    target.appendChild(el);
    return {
      update: (next: Partial<WidgetOptions>) => {
        if (next.site) el.setAttribute('site', next.site);
        if (next.center) {
          const c =
            typeof next.center === 'string'
              ? next.center
              : `${next.center.lat},${next.center.lng}`;
          el.setAttribute('center', c);
        }
        if (next.filters) {
          const f = Array.isArray(next.filters) ? next.filters.join(',') : next.filters;
          el.setAttribute('filters', f);
        }
      },
      destroy: () => el.remove(),
    };
  },
};

(globalThis as unknown as { __apf: ApfGlobal }).__apf = apfApi;
